import React, { useEffect, useState, useCallback } from "react";
import {
  Card, Badge, Button, Spinner, Alert, Form, Modal, Table,
} from "react-bootstrap";
import { useParams, useHistory } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { FaWhatsapp } from "react-icons/fa";
import { MdEmail, MdPhone, MdChat } from "react-icons/md";
import { getTokenFromLocalStorage } from "utilities/tokenUtils";
import { getUserFromLocalStorage } from "utilities/user.js";
import ConfirmDialog, { useConfirmDialog } from "../../components/ConfirmDialog";
import { useGate } from "../../components/team/Gated";
import AppSelect from "../../components/ui/AppSelect";

const API_URL = process.env.REACT_APP_SERVER_URL;

const CHANNEL_META = {
  IN_PLATFORM: { Icon: MdChat,    label: "On Lusuku",  color: "#0a578b" },
  WHATSAPP:    { Icon: FaWhatsapp, label: "WhatsApp",   color: "#25D366" },
  EMAIL:       { Icon: MdEmail,    label: "Email",      color: "#0a578b" },
  PHONE:       { Icon: MdPhone,    label: "Phone",      color: "#0a578b" },
};

const STATUS_COLORS = {
  OPEN:               "warning",
  CLOSED:             "secondary",
  SENT:               "info",
  COUNTER_OFFERED:    "primary",
  REVISION_REQUESTED: "warning",
  ACCEPTED:           "success",
  REJECTED:           "danger",
  EXPIRED:            "dark",
  SUPERSEDED:         "light",
  AWAITING_VENDOR:    "warning",
  QUOTED:             "info",
  NEGOTIATING:        "primary",
  FULFILLED:          "success",
  DECLINED:           "danger",
  CANCELLED:          "secondary",
};

const STAGE_META = [
  { key: "AWAITING_VENDOR_FULFILMENT", label: "Awaiting confirmation" },
  { key: "VENDOR_CONFIRMED",           label: "Confirmed"             },
  { key: "DISPATCHED",                 label: "Dispatched"            },
  { key: "DELIVERED",                  label: "Delivered"             },
];

const fmt = (d) => d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const fmtUgx = (n, cur = "UGX") => n != null ? `${cur} ${Number(n).toLocaleString("en-UG")}` : "—";

// ── Empty line-item template ─────────────────────────────────────────────────
const emptyLineItem = () => ({ description: "", quantity: 1, unitPrice: 0 });
const emptyForm = () => ({
  lineItems:    [emptyLineItem()],
  notes:        "",
  validUntil:   "",
  currency:     "UGX",
  paymentTerms: "",
  leadTime:     "",
  shippingCost: 0,
  vatPct:       0,
});

// ── Stage progress bar ───────────────────────────────────────────────────────
const StageStepper = ({ stage }) => {
  const idx = STAGE_META.findIndex((s) => s.key === stage);
  return (
    <div style={{ display: "flex", gap: 0, marginBottom: 16 }}>
      {STAGE_META.map((s, i) => {
        const done    = i <= idx;
        const current = i === idx;
        return (
          <div key={s.key} style={{ flex: 1, textAlign: "center" }}>
            <div style={{
              height: 6,
              background: done ? "#0a578b" : "#e2e8f0",
              borderRadius: i === 0 ? "3px 0 0 3px" : i === STAGE_META.length - 1 ? "0 3px 3px 0" : 0,
              marginBottom: 4,
            }} />
            <div style={{ fontSize: "0.68rem", color: current ? "#0a578b" : done ? "#475569" : "#94a3b8", fontWeight: current ? 700 : 400 }}>
              {s.label}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ── Main component ───────────────────────────────────────────────────────────
const VendorQuoteRequestDetail = () => {
  const { id: requestId } = useParams();
  const history            = useHistory();
  const token              = getTokenFromLocalStorage();
  const user               = getUserFromLocalStorage();
  const authHeaders        = token ? { Authorization: `Bearer ${token}` } : {};

  const [data, setData]           = useState(null);   // { request, brand, quotes }
  const [linkedOrder, setLinkedOrder] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);

  // Quote form
  const [showForm, setShowForm]         = useState(false);
  const [quoteForm, setQuoteForm]       = useState(emptyForm());
  const [submitting, setSubmitting]     = useState(false);

  // Stage panel (vendor advances order)
  const [stageBusy, setStageBusy] = useState(false);
  const [requestBusy, setRequestBusy] = useState(false);
  // Vendor team permissions: replying, declining and resolving a quote
  // request is quotes:manage; moving the linked order is orders:manage.
  const quoteGate = useGate("quotes:manage");
  const ordersGate = useGate("orders:manage");

  // ── Redirect to login if not authenticated ──────────────────────────────
  useEffect(() => {
    if (!token) {
      history.push(`/auth/login-page?redirect=/vendor/quote-requests/${requestId}`);
    }
  }, [token, requestId, history]);

  // ── Fetch data ──────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API_URL}/api/brands/quote-requests/${requestId}`,
        { headers: authHeaders }
      );
      const d = res.data?.data;
      setData(d);

      // If there's an accepted quote, fetch its linked order
      const accepted = d?.quotes?.find((q) => q.status === "ACCEPTED");
      if (accepted?.acceptedOrderId) {
        try {
          const oRes = await axios.get(
            `${API_URL}/api/brands/quotes/${accepted._id}/order`,
            { headers: authHeaders }
          );
          setLinkedOrder(oRes.data?.data || null);
        } catch (_) {}
      }
    } catch (e) {
      const msg = e.response?.data?.message || "Failed to load quote request";
      setError(msg);
      if (e.response?.status === 403) {
        setError("You do not have permission to view this quote request.");
      }
    } finally {
      setLoading(false);
    }
  }, [requestId, token]);

  useEffect(() => { if (token) fetchData(); }, [fetchData, token]);

  // ── Quote form helpers ───────────────────────────────────────────────────
  const addLine    = () => setQuoteForm((f) => ({ ...f, lineItems: [...f.lineItems, emptyLineItem()] }));
  const removeLine = (i) => setQuoteForm((f) => ({ ...f, lineItems: f.lineItems.filter((_, idx) => idx !== i) }));
  const updateLine = (i, key, val) => setQuoteForm((f) => {
    const li = [...f.lineItems]; li[i] = { ...li[i], [key]: val }; return { ...f, lineItems: li };
  });

  const subtotal   = quoteForm.lineItems.reduce((s, li) => s + (Number(li.quantity) || 0) * (Number(li.unitPrice) || 0), 0);
  const vatAmount  = Math.round(subtotal * (Number(quoteForm.vatPct) || 0) / 100);
  const totalAmount = subtotal + vatAmount + (Number(quoteForm.shippingCost) || 0);

  const submitQuote = async () => {
    setSubmitting(true);
    try {
      const structuredNotes = [
        quoteForm.paymentTerms && `Payment terms: ${quoteForm.paymentTerms}`,
        quoteForm.leadTime     && `Estimated lead time: ${quoteForm.leadTime}`,
        quoteForm.notes        && quoteForm.notes,
      ].filter(Boolean).join("\n");

      const allLineItems = [
        ...quoteForm.lineItems.map((li) => ({ ...li, quantity: Number(li.quantity), unitPrice: Number(li.unitPrice) })),
        ...(Number(quoteForm.shippingCost) > 0 ? [{ description: "Shipping / Freight", quantity: 1, unitPrice: Number(quoteForm.shippingCost) }] : []),
        ...(Number(quoteForm.vatPct) > 0        ? [{ description: `VAT (${quoteForm.vatPct}%)`,      quantity: 1, unitPrice: vatAmount }] : []),
      ];

      await axios.post(
        `${API_URL}/api/brands/quote-requests/${requestId}/quotes`,
        { lineItems: allLineItems, totalAmount, currency: quoteForm.currency, validUntil: quoteForm.validUntil, notes: structuredNotes },
        { headers: authHeaders }
      );
      toast.success("Quote sent to buyer!");
      setShowForm(false);
      setQuoteForm(emptyForm());
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to send quote");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Advance order stage ──────────────────────────────────────────────────
  const advanceStage = async (stage) => {
    if (!linkedOrder) return;
    setStageBusy(true);
    try {
      await axios.patch(
        `${API_URL}/api/brands/orders/${linkedOrder._id}/stage`,
        { stage },
        { headers: authHeaders }
      );
      toast.success(`Order marked as ${stage.replace(/_/g, " ").toLowerCase()}`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to update stage");
    } finally {
      setStageBusy(false);
    }
  };

  const declineRequest = async () => {
    const reason = window.prompt(
      "Why can this request not be fulfilled?",
      "Unable to fulfil this request"
    );
    if (reason === null) return;
    setRequestBusy(true);
    try {
      await axios.post(
        `${API_URL}/api/brands/quote-requests/${requestId}/decline`,
        { reason },
        { headers: authHeaders }
      );
      toast.success("Request closed and buyer notified.");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to close request");
    } finally {
      setRequestBusy(false);
    }
  };

  const confirmDialog = useConfirmDialog();

  const resolveExternalRequest = () => {
    confirmDialog.ask({
      title: "Mark this request as completed?",
      body: "This request was handled off-platform. Closing it removes it from the open queue; the customer is not notified.",
      tone: "primary",
      confirmLabel: "Mark completed",
      busyLabel: "Closing...",
      onConfirm: async () => {
        setRequestBusy(true);
        try {
          await axios.post(
            `${API_URL}/api/brands/quote-requests/${requestId}/resolve`,
            {},
            { headers: authHeaders }
          );
          toast.success("Request marked as completed.");
          fetchData();
        } catch (e) {
          toast.error(e.response?.data?.message || "Failed to resolve request");
        } finally {
          setRequestBusy(false);
        }
      },
    });
  };

  // ── Render ───────────────────────────────────────────────────────────────
  if (!token) return null;

  if (loading) {
    return (
      <div className="content d-flex justify-content-center align-items-center" style={{ minHeight: 300 }}>
        <Spinner animation="border" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="content">
        <Alert variant="danger">{error}</Alert>
        <Button variant="outline-secondary" size="sm" onClick={() => history.goBack()}>← Back</Button>
      </div>
    );
  }

  const { request, brand, quotes = [] } = data || {};
  const latestQuote  = quotes[0];
  const requestStatus =
    request?.lifecycleStatus ||
    (request?.status === "CLOSED" ? "QUOTED" : request?.status);
  const isOpen       = requestStatus === "AWAITING_VENDOR";
  const requestActive = ["AWAITING_VENDOR", "QUOTED", "NEGOTIATING"].includes(requestStatus);
  const chMeta       = CHANNEL_META[request?.preferredChannel] || CHANNEL_META.IN_PLATFORM;
  const ChIcon       = chMeta.Icon;
  const buyerName    = request?.contactSnapshot?.name  || request?.buyerId?.name  || "—";
  const buyerEmail   = request?.contactSnapshot?.email || request?.buyerId?.email;
  const buyerPhone   = request?.contactSnapshot?.phone || request?.buyerId?.phone;
  const responseDueAt = request?.responseDueAt
    ? new Date(request.responseDueAt)
    : null;
  const responseOverdue =
    isOpen && responseDueAt && responseDueAt.getTime() < Date.now();

  const STAGE_ACTIONS = [
    { from: "AWAITING_VENDOR_FULFILMENT", label: "Confirm Order",    stage: "VENDOR_CONFIRMED", variant: "primary"  },
    { from: "VENDOR_CONFIRMED",           label: "Mark Dispatched",  stage: "DISPATCHED",       variant: "info"     },
    { from: "DISPATCHED",                 label: "Mark Delivered",   stage: "DELIVERED",        variant: "success"  },
  ];

  const currentStageAction = linkedOrder
    ? STAGE_ACTIONS.find((a) => a.from === linkedOrder.brandFlowStage)
    : null;

  const canCancel = linkedOrder && !["DELIVERED", "CANCELLED"].includes(linkedOrder.brandFlowStage);

  return (
    <div className="content">
      <ConfirmDialog {...confirmDialog.props} />
      {/* ── Header ── */}
      <div className="d-flex align-items-center gap-3 mb-3 flex-wrap">
        <Button variant="outline-secondary" size="sm" onClick={() => history.push("/admin/brand-quotes")}>
          ← Back to Quotes
        </Button>
        <h4 className="mb-0">{request?.title || "Quote Request"}</h4>
        <Badge bg={STATUS_COLORS[requestStatus] || "secondary"}>{requestStatus?.replace(/_/g, " ")}</Badge>
      </div>

      <div className="row g-3">
        {/* ── Left: request detail ── */}
        <div className="col-lg-7">
          <Card className="mb-3">
            <Card.Header><strong>Request details</strong></Card.Header>
            <Card.Body>
              <div className="row g-2" style={{ fontSize: "0.88rem" }}>
                <div className="col-6">
                  <div className="text-muted small text-uppercase">Buyer</div>
                  <div className="fw-semibold">{buyerName}</div>
                  {buyerEmail && <div className="text-muted">{buyerEmail}</div>}
                  {buyerPhone && <div className="text-muted">{buyerPhone}</div>}
                </div>
                <div className="col-6">
                  <div className="text-muted small text-uppercase">Preferred channel</div>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "#eff6ff", borderRadius: 10, padding: "2px 10px", border: "1px solid #bfdbfe", color: chMeta.color, fontSize: "0.78rem", fontWeight: 600 }}>
                    <ChIcon size={13} color={chMeta.color} />
                    {chMeta.label}
                  </span>
                </div>
                {responseDueAt && (
                  <div className="col-6">
                    <div className="text-muted small text-uppercase">First response due</div>
                    <div className={responseOverdue ? "text-danger fw-semibold" : ""}>
                      {responseDueAt.toLocaleString()}
                    </div>
                    {request.vendorReminderSentAt && (
                      <div className="small text-danger">Reminder and admin escalation sent</div>
                    )}
                  </div>
                )}
                {request?.desiredDeadline && (
                  <div className="col-6">
                    <div className="text-muted small text-uppercase">Desired deadline</div>
                    <div>{fmt(request.desiredDeadline)}</div>
                  </div>
                )}
                {request?.productSnapshot?.name && (
                  <div className="col-6">
                    <div className="text-muted small text-uppercase">Product</div>
                    <div>{request.productSnapshot.name}{request.productSnapshot.skuCode ? ` (${request.productSnapshot.skuCode})` : ""}</div>
                  </div>
                )}
                {request?.message && (
                  <div className="col-12">
                    <div className="text-muted small text-uppercase">Message</div>
                    <div style={{ whiteSpace: "pre-line" }}>{request.message}</div>
                  </div>
                )}
                {request?.specs && (
                  <div className="col-12">
                    <div className="text-muted small text-uppercase">Specifications</div>
                    <div style={{ whiteSpace: "pre-line" }}>{request.specs}</div>
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>

          {/* ── Quote history ── */}
          {quotes.length > 0 && (
            <Card className="mb-3">
              <Card.Header><strong>Quote history ({quotes.length} round{quotes.length > 1 ? "s" : ""})</strong></Card.Header>
              <Card.Body className="p-0">
                <Table size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>Round</th>
                      <th>Status</th>
                      <th>Total</th>
                      <th>Valid until</th>
                      <th>Counter-offer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {quotes.map((q, i) => (
                      <tr key={q._id}>
                        <td>{quotes.length - i}</td>
                        <td><Badge bg={STATUS_COLORS[q.status] || "secondary"}>{q.status}</Badge></td>
                        <td>{fmtUgx(q.totalAmount, q.currency)}</td>
                        <td>{fmt(q.validUntil)}</td>
                        <td>
                          {q.counterOffer?.amount
                            ? <span className="text-primary fw-semibold">{fmtUgx(q.counterOffer.amount, q.counterOffer.currency)}</span>
                            : "—"
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}

          {/* ── Revision history ── */}
          {latestQuote?.revisionHistory?.length > 0 && (
            <Card className="mb-3">
              <Card.Header><strong>Negotiation audit trail</strong></Card.Header>
              <Card.Body className="p-0">
                <Table size="sm" className="mb-0">
                  <thead><tr><th>Action</th><th>By</th><th>Note</th><th>Amount</th><th>Date</th></tr></thead>
                  <tbody>
                    {latestQuote.revisionHistory.map((h, i) => (
                      <tr key={i}>
                        <td><Badge bg={h.action === "REJECTED" ? "danger" : h.action === "COUNTER_OFFERED" ? "primary" : "warning"}>{h.action}</Badge></td>
                        <td style={{ fontSize: "0.75rem" }}>{String(h.by).slice(-6)}</td>
                        <td style={{ fontSize: "0.78rem" }}>{h.note || "—"}</td>
                        <td>{h.amount ? fmtUgx(h.amount) : "—"}</td>
                        <td style={{ fontSize: "0.75rem" }}>{fmt(h.at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}

          {request?.activityLog?.length > 0 && (
            <Card className="mb-3">
              <Card.Header><strong>Request lifecycle</strong></Card.Header>
              <Card.Body className="p-0">
                <Table size="sm" className="mb-0">
                  <thead><tr><th>Event</th><th>Actor</th><th>Note</th><th>Date</th></tr></thead>
                  <tbody>
                    {[...request.activityLog].reverse().map((entry, index) => (
                      <tr key={`${entry.action}-${entry.at}-${index}`}>
                        <td><Badge bg="light" text="dark">{entry.action?.replace(/_/g, " ")}</Badge></td>
                        <td style={{ fontSize: "0.75rem" }}>{entry.actorRole || "SYSTEM"}</td>
                        <td style={{ fontSize: "0.78rem" }}>{entry.note || "—"}</td>
                        <td style={{ fontSize: "0.75rem" }}>{fmt(entry.at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          )}
        </div>

        {/* ── Right: actions + order tracking ── */}
        <div className="col-lg-5">
          {requestActive && (
            <Card className="mb-3">
              <Card.Header><strong>Close the loop</strong></Card.Header>
              <Card.Body>
                <p className="text-muted small">
                  Keep the buyer informed. Decline requests you cannot fulfil,
                  or mark an off-platform conversation completed once it is resolved.
                </p>
                <div className="d-flex gap-2 flex-wrap">
                  {request?.preferredChannel !== "IN_PLATFORM" && (
                    <Button
                      variant="outline-success"
                      size="sm"
                      disabled={requestBusy || !quoteGate.allowed}
                      title={quoteGate.title}
                      onClick={resolveExternalRequest}
                    >
                      Mark resolved
                    </Button>
                  )}
                  <Button
                    variant="outline-danger"
                    size="sm"
                    disabled={requestBusy || !quoteGate.allowed}
                    title={quoteGate.title}
                    onClick={declineRequest}
                  >
                    Decline request
                  </Button>
                </div>
              </Card.Body>
            </Card>
          )}

          {!requestActive && request?.resolution && (
            <Alert variant={requestStatus === "FULFILLED" ? "success" : "secondary"}>
              <strong>{requestStatus?.replace(/_/g, " ")}</strong>
              {request.resolution.note && <div className="small mt-1">{request.resolution.note}</div>}
            </Alert>
          )}

          {/* Send quote */}
          {isOpen && (
            <Card className="mb-3 border-primary">
              <Card.Header className="bg-primary text-white"><strong>Respond with a quote</strong></Card.Header>
              <Card.Body>
                <p className="text-muted small">Fill in your line items and send a formal quote to the buyer.</p>
                <Button variant="primary" className="w-100" {...quoteGate.disabledProps} onClick={() => setShowForm(true)}>
                  Open quote form
                </Button>
              </Card.Body>
            </Card>
          )}

          {/* Counter-offer notice */}
          {latestQuote?.status === "COUNTER_OFFERED" && (
            <Card className="mb-3 border-warning">
              <Card.Header className="bg-warning text-dark"><strong>Counter-offer received</strong></Card.Header>
              <Card.Body style={{ fontSize: "0.88rem" }}>
                <div><strong>Buyer proposes:</strong> {fmtUgx(latestQuote.counterOffer?.amount, latestQuote.counterOffer?.currency)}</div>
                {latestQuote.counterOffer?.message && <div className="mt-1"><strong>Note:</strong> {latestQuote.counterOffer.message}</div>}
                <div className="mt-2 d-flex gap-2">
                  <Button variant="primary" size="sm" {...quoteGate.disabledProps} onClick={() => setShowForm(true)}>Send revised quote</Button>
                </div>
              </Card.Body>
            </Card>
          )}

          {/* Revision requested notice */}
          {latestQuote?.status === "REVISION_REQUESTED" && (
            <Card className="mb-3 border-warning">
              <Card.Header className="bg-warning text-dark"><strong>Revision requested</strong></Card.Header>
              <Card.Body style={{ fontSize: "0.88rem" }}>
                {latestQuote.revisionNote && <div><strong>Buyer note:</strong> {latestQuote.revisionNote}</div>}
                <Button variant="primary" size="sm" className="mt-2 w-100" {...quoteGate.disabledProps} onClick={() => setShowForm(true)}>Send revised quote</Button>
              </Card.Body>
            </Card>
          )}

          {/* Order tracking (post-acceptance) */}
          {linkedOrder && (
            <Card className="mb-3">
              <Card.Header><strong>Order fulfilment</strong> <Badge bg="secondary" className="ms-1">{linkedOrder.brandFlowStage}</Badge></Card.Header>
              <Card.Body>
                <StageStepper stage={linkedOrder.brandFlowStage} />
                <div className="d-flex gap-2 flex-wrap">
                  {currentStageAction && (
                    <Button
                      variant={currentStageAction.variant}
                      size="sm"
                      disabled={stageBusy || !ordersGate.allowed}
                      title={ordersGate.title}
                      onClick={() => advanceStage(currentStageAction.stage)}
                    >
                      {stageBusy ? <Spinner size="sm" animation="border" /> : currentStageAction.label}
                    </Button>
                  )}
                  {canCancel && (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      disabled={stageBusy || !ordersGate.allowed}
                      title={ordersGate.title}
                      onClick={() => advanceStage("CANCELLED")}
                    >
                      Cancel Order
                    </Button>
                  )}
                </div>
                {linkedOrder.brandFlowStage === "DELIVERED" && (
                  <Alert variant="success" className="mt-3 mb-0 py-2 small">Order delivered ✓</Alert>
                )}
                {linkedOrder.brandFlowStage === "CANCELLED" && (
                  <Alert variant="danger" className="mt-3 mb-0 py-2 small">Order cancelled</Alert>
                )}
              </Card.Body>
            </Card>
          )}

          {/* Channel contact shortcut */}
          {(buyerPhone || buyerEmail) && (
            <Card className="mb-3">
              <Card.Header><strong>Contact buyer</strong></Card.Header>
              <Card.Body className="d-flex flex-column gap-2">
                {buyerPhone && (
                  <Button
                    as="a"
                    href={`https://wa.me/${String(buyerPhone).replace(/\D/g, "")}?text=${encodeURIComponent(`Hi ${buyerName}, this is regarding your Lusuku quote request "${request?.title || ""}".`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="success"
                    size="sm"
                  >
                    <FaWhatsapp className="me-1" /> WhatsApp {buyerName}
                  </Button>
                )}
                {buyerEmail && (
                  <Button
                    as="a"
                    href={`mailto:${buyerEmail}?subject=Re: ${encodeURIComponent(request?.title || "Your Lusuku quote")}`}
                    variant="outline-secondary"
                    size="sm"
                  >
                    <MdEmail className="me-1" /> Email {buyerName}
                  </Button>
                )}
              </Card.Body>
            </Card>
          )}
        </div>
      </div>

      {/* ── Quote form modal ── */}
      <Modal show={showForm} onHide={() => setShowForm(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {latestQuote?.status === "COUNTER_OFFERED" || latestQuote?.status === "REVISION_REQUESTED"
              ? "Send revised quote"
              : "Send quote to buyer"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <h6 className="text-muted mb-2">Line items</h6>
          {quoteForm.lineItems.map((li, idx) => (
            <div key={idx} className="d-flex gap-2 mb-2 align-items-center">
              <Form.Control size="sm" placeholder="Description" value={li.description} onChange={(e) => updateLine(idx, "description", e.target.value)} style={{ flex: 3 }} />
              <Form.Control size="sm" type="number" placeholder="Qty" min={1} value={li.quantity} onChange={(e) => updateLine(idx, "quantity", e.target.value)} style={{ flex: 1 }} />
              <Form.Control size="sm" type="number" placeholder="Unit price" min={0} value={li.unitPrice} onChange={(e) => updateLine(idx, "unitPrice", e.target.value)} style={{ flex: 2 }} />
              <Button variant="outline-danger" size="sm" onClick={() => removeLine(idx)} disabled={quoteForm.lineItems.length === 1}>×</Button>
            </div>
          ))}
          <Button variant="outline-secondary" size="sm" className="mb-3" onClick={addLine}>+ Add line</Button>

          <div className="row g-2 mb-3">
            <div className="col-md-6">
              <Form.Label className="small">Shipping / Freight ({quoteForm.currency})</Form.Label>
              <Form.Control size="sm" type="number" min={0} value={quoteForm.shippingCost} onChange={(e) => setQuoteForm((f) => ({ ...f, shippingCost: e.target.value }))} />
            </div>
            <div className="col-md-6">
              <Form.Label className="small">VAT %</Form.Label>
              <Form.Control size="sm" type="number" min={0} max={100} value={quoteForm.vatPct} onChange={(e) => setQuoteForm((f) => ({ ...f, vatPct: e.target.value }))} />
            </div>
          </div>

          <div className="text-end fw-bold mb-3">
            Total: {quoteForm.currency} {totalAmount.toLocaleString()}
          </div>

          <div className="row g-2 mb-3">
            <div className="col-md-4">
              <Form.Label className="small">Valid until</Form.Label>
              <Form.Control size="sm" type="date" min={new Date().toISOString().slice(0, 10)} value={quoteForm.validUntil} onChange={(e) => setQuoteForm((f) => ({ ...f, validUntil: e.target.value }))} />
            </div>
            <div className="col-md-4">
              <Form.Label className="small">Currency</Form.Label>
              <AppSelect block size="sm" value={quoteForm.currency} onChange={(e) => setQuoteForm((f) => ({ ...f, currency: e.target.value }))}>
                <option value="UGX">UGX</option>
                <option value="USD">USD</option>
              </AppSelect>
            </div>
            <div className="col-md-4">
              <Form.Label className="small">Lead time</Form.Label>
              <Form.Control size="sm" placeholder="e.g. 5-7 working days" value={quoteForm.leadTime} onChange={(e) => setQuoteForm((f) => ({ ...f, leadTime: e.target.value }))} />
            </div>
          </div>

          <Form.Group className="mb-3">
            <Form.Label className="small">Payment terms</Form.Label>
            <AppSelect block size="sm" value={quoteForm.paymentTerms} onChange={(e) => setQuoteForm((f) => ({ ...f, paymentTerms: e.target.value }))}>
              <option value="">Select…</option>
              <option value="100% upfront">100% upfront</option>
              <option value="50% deposit, 50% on delivery">50% deposit, 50% on delivery</option>
              <option value="Net 7">Net 7</option>
              <option value="Net 14">Net 14</option>
              <option value="Net 30">Net 30</option>
              <option value="On delivery">On delivery</option>
            </AppSelect>
          </Form.Group>

          <Form.Group>
            <Form.Label className="small">Notes to buyer (optional)</Form.Label>
            <Form.Control as="textarea" size="sm" rows={2} value={quoteForm.notes} onChange={(e) => setQuoteForm((f) => ({ ...f, notes: e.target.value }))} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowForm(false)}>Cancel</Button>
          <Button
            variant="primary"
            onClick={submitQuote}
            disabled={submitting || !quoteGate.allowed || quoteForm.lineItems.some((li) => !li.description.trim())}
            title={quoteGate.title}
          >
            {submitting ? <><Spinner size="sm" animation="border" className="me-1" />Sending…</> : "Send quote to buyer"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default VendorQuoteRequestDetail;
